import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { isEmpty, get } from 'lodash';
import { InformationPopUp, FormSpacer, Form, CmsAddressSearch } from 'components';
import { FormGroupLabel, Button } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { FormStepContentProps } from 'types';
import { useScrollTop } from 'hooks/useScrollTop';
import { useSearchAddress } from 'hooks/useSearchAddress';
import { FormDataType, useFormState } from 'contexts/FormProvider';
import validationSchema, {
  isRelationshipNeedsPreferredContact,
  contactReasonSchema,
  yourDetailsSchema,
  contactDetailsSchema,
} from './schema';
import ContactPreferredMethod from 'pages/common/components/ContactPreferredMethod';
import { useContentContext } from '@qbe/dfc-content';
import { AUSTRALIA, MIN_ADDRESS_SEARCH_STRING, YesNoList } from 'utils/constants';

import { RELATIONSHIPS } from './enum';
import { useCheckWindowCMS } from 'hooks/useCheckWindowCMS';
import { AddressAutocomplete } from 'pages/common/components/AddressAutocomplete';
import { formatNext, TrackEvent } from 'hooks/useAnalytics';
import { usePrevious } from 'hooks/usePrevious';
import { TextFieldController, DropdownController, FirstnameLastname, AddressManual } from 'controllers';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIsFieldRequired } from 'utils/isFieldRequired';
import { useContentComplaintPartners } from 'utils/content';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AddressType } from 'components/AddressSearch/addressTypes';
import useAddressChangeHandler from 'hooks/useAddressChangeHandler';
import useAddressDefaults from 'hooks/useAddressDefaults';

const ConsentForm = ({ onComplete, onStart }: FormStepContentProps) => {
  useScrollTop();
  useCheckWindowCMS();

  const { formData } = useFormState();

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const { DcomPrtrQasMigrationToggle = true } = useFlags();

  const form = useForm({
    mode: 'onBlur',
    defaultValues: formData,
    resolver: yupResolver(validationSchema) as any,
  });

  const { isFieldRequired } = useIsFieldRequired(form, validationSchema);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues,
    register,
    trigger: triggerValidation,
    clearErrors: clearError,
    setError,
  } = form;

  useEffect(() => {
    isDirty && onStart();
  }, [isDirty, onStart]);

  const isValid = validationSchema.isValidSync(watch()); // temporary fix for radio button issue

  const [showNextButton, setShowNextButton] = useState(false);
  const [showContactNextButton, setShowContactNextButton] = useState(false);
  const [personalDetails, setPersonalDetails] = React.useState(false);
  const [contactDetails, setContactDetails] = React.useState(false);
  const isContactReasonValid = contactReasonSchema.isValidSync(watch());
  const isYourDetailsValid = yourDetailsSchema.isValidSync(watch());
  const isValidContactDetails = contactDetailsSchema.isValidSync(watch());

  const policyOrClaim = watch('policyOrClaim') || '';
  const policyNumber = watch('policyNumber') || '';
  const claimNumber = watch('claimNumber') || '';

  const relationship = watch('relationship');
  const organisation = watch('organisation') || '';
  const contactType = watch('contactType') || '';

  const values: object = watch();
  const contactPreferedMethod = watch('contactPreferedMethod') || null;
  const isManualAddressEnabled = watch('isManualAddressEnabled');
  const isCountryAus = watch('country') === AUSTRALIA;
  const country = watch('country');
  const state = watch('state');
  const postcode = watch('postcode');

  const handleSubmitContactReason = () => {
    setShowNextButton(true);
    setPersonalDetails(true);
    TrackEvent(formatNext('nextContactReason'), getValues());
  };

  const handleKeyDownPolicyClaimNumber = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((policyOrClaim === 'policy' && isEmpty(policyNumber)) || (policyOrClaim === 'claim' && isEmpty(claimNumber))) {
      clearError(['relationship', 'firstName', 'lastName', 'organisation', 'phoneNumber', 'emailAddress']);

      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if ((!isEmpty(policyNumber) || !isEmpty(claimNumber)) && isContactReasonValid) {
        handleSubmitContactReason();

        triggerValidation(['policyNumber', 'claimNumber']);
      }
    }
  };

  const isPostalAddressEnabled = isRelationshipNeedsPreferredContact(relationship);
  const showContactMethod = contactType === 'yes' ? true : false;

  const { addressList, error: addressError, searchValue, searching } = useSearchAddress();

  const prefferedMethod = watch(['contactPreferedMethod', 'contactType']);

  const handlePostalAddressChange = useAddressChangeHandler(AddressType.Postal, form);

  const getPostalAddressDefaults = useAddressDefaults(AddressType.Postal, form);

  const options = [
    {
      text: getTextContent('consentForm.options.0.text'),
      value: 'claim',
    },
    {
      text: getTextContent('consentForm.options.1.text'),
      value: 'policy',
    },
  ];

  function onSubmit(data: Partial<FormDataType>) {
    let newData = data;

    if (newData.contactType === 'no') {
      newData = {
        ...newData,
        postalAddressObject: null,
        isManualAddressEnabled: false,
      };
    }
    onComplete({ ...formData, ...newData });
  }

  // todo 5: check if this useEffect can be deleted later
  React.useEffect(() => {
    if (!DcomPrtrQasMigrationToggle) {
      return;
    }
    if (isManualAddressEnabled && isCountryAus) {
      register('state');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManualAddressEnabled, isCountryAus]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)} data-test="consent-form">
      <FormCard title={getTextContent('consent.insured.form.heading')} description="" variant="pageTitle" />
      <FormCard title={getTextContent('consent.contactReason.heading')} variant="sectionTitle">
        <Controller
          name="policyOrClaim"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Toggle
              {...field}
              required
              defaultValue={policyOrClaim}
              title={getTextContent('consent.contactReason.sectionHeading')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setValue('policyOrClaim', value);
                triggerValidation('policyOrClaim');
                return value;
              }}
              optionList={options.map((option) => ({
                autoComplete: 'off',
                id: option.value,
                value: option.value,
                label: option.text ?? (option as any).value,
                defaultChecked: policyOrClaim === option.value,
                'data-test': 'policyOrClaim',
                'data-track': 'policyOrClaim',
                'data-track-value': 'capture',
              }))}
            />
          )}
        />

        {policyOrClaim === 'claim' && (
          <>
            <FormGroupLabel
              alignCenter
              required={isFieldRequired('claimNumber')}
              title={getTextContent('consent.contactReason.claimNumber')}
              legendElementOveride
              titleSuffix={
                <InformationPopUp
                  message={getTextContent('consent.contactReason.claimNumber.infoText')}
                  data-track="infoIconWhatIsClaimNumber"
                />
              }
              subtitle={getTextContent('consent.contactReason.contactInformation')}
            />
            <FormSpacer type="input" />

            <TextFieldController
              control={control}
              maxLength={50}
              name="claimNumber"
              label="Claim number"
              inputProps={{
                minLength: 4,
                maxLength: 50,
              }}
              capture
              onKeyDown={handleKeyDownPolicyClaimNumber}
            />
          </>
        )}

        {policyOrClaim === 'policy' && (
          <>
            <FormGroupLabel
              alignCenter
              required={isFieldRequired('policyNumber')}
              title={getTextContent('consent.contactReason.policyNumber')}
              legendElementOveride
              titleSuffix={
                <InformationPopUp
                  message={getTextContent('consent.contactReason.policyNumber.infoText')}
                  data-track="infoIconWhatIsPolicyNumber"
                />
              }
              subtitle={getTextContent('consent.contactReason.contactInformation')}
            />
            <FormSpacer type="input" />
            <TextFieldController
              control={control}
              maxLength={50}
              name="policyNumber"
              label="Policy number"
              inputProps={{
                minLength: 4,
                maxLength: 100,
              }}
              capture
              onKeyDown={handleKeyDownPolicyClaimNumber}
            />
          </>
        )}

        {!showNextButton && (
          <>
            <FormGroupLabel
              alignCenter
              title={''}
              subtitle={getTextContent('consent.contactReason.webformInformation')}
            />
          </>
        )}

        {!showNextButton && (
          <>
            <FormSpacer type="input" />

            <Button
              label="Next"
              onClick={handleSubmitContactReason}
              disabled={!isContactReasonValid}
              data-test="contact-reason-next"
            />
          </>
        )}
      </FormCard>

      {showNextButton && (
        <>
          <FormCard title={getTextContent('consent.personalDetails.heading')} variant="sectionTitle">
            <FormGroupLabel
              alignCenter
              required={isFieldRequired('relationship')}
              title={getTextContent('consent.personalDetails.relationship')}
              legendElementOveride
              titleSuffix={
                <InformationPopUp
                  message={getTextContent('consent.personalDetails.relationshipInfoText')}
                  data-track="infoIconRelationship"
                />
              }
            />
            <FormSpacer type="input" />
            <DropdownController
              control={control}
              name="relationship"
              label="Relationship to QBE"
              capture
              optionList={RELATIONSHIPS.map((value) => ({
                label: value,
                value,
              }))}
            />

            <FirstnameLastname
              form={form}
              maxLength={50}
              slotProps={{
                titleField: {
                  title: getTextContent('consent.personalDetails.name'),
                },
              }}
            />

            <FormGroupLabel
              alignCenter
              legendElementOveride
              required={isFieldRequired('organisation')}
              title={getTextContent('consent.personalDetails.organisation')}
            />
            <FormSpacer type="input" />

            <TextFieldController
              control={control}
              maxLength={100}
              name="organisation"
              label={getTextContent('consent.personalDetails.organisation.label')}
              inputProps={{
                minLength: 1,
                maxLength: 100,
              }}
              capture
            />
            <FormSpacer type="formgroup" />
            {personalDetails && !showContactNextButton && (
              <Button
                label="Next"
                onClick={() => {
                  setShowContactNextButton(true);
                  setContactDetails(true);
                  TrackEvent(formatNext('nextYourDetails'), getValues());
                }}
                disabled={!isYourDetailsValid}
              />
            )}
          </FormCard>
        </>
      )}
      {showContactNextButton && (
        <>
          <FormCard title={getTextContent('consent.contactDetails.heading')} variant="sectionTitle">
            <FormGroupLabel
              alignCenter
              legendElementOveride
              required={isFieldRequired('emailAddress')}
              title={getTextContent('consent.contactDetails.email')}
            />
            <FormSpacer type="input" />
            <TextFieldController
              control={control}
              label={getTextContent('consent.contactDetails.emailAddress.label')}
              name="emailAddress"
              maxLength={100}
              inputProps={{
                maxLength: 100,
              }}
              capture
            />

            <FormGroupLabel
              alignCenter
              legendElementOveride
              title={getTextContent('consent.contactDetails.phoneNumber')}
              required={isFieldRequired('phoneNumber')}
            />
            <FormSpacer type="input" />

            <TextFieldController
              control={control}
              label={getTextContent('consent.contactDetails.phoneNumber.label')}
              name="phoneNumber"
              inputProps={{
                maxLength: 20,
              }}
              capture
            />

            {isPostalAddressEnabled && (
              <>
                <Controller
                  name="contactType"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <Toggle
                      {...field}
                      required
                      title={getTextContent('consent.contactDetails.contactTypeField.information')}
                      subtitle={getTextContent('consent.contactDetails.contactTypeField.details')}
                      optionList={YesNoList.map((option) => ({
                        autoComplete: 'off',
                        id: option.value,
                        value: option.value,
                        label: option.text ?? option.value,
                        defaultChecked: contactType === option.value,
                      }))}
                      onChange={(e: any) => {
                        const value = e.target.value;
                        setValue('contactType', value, { shouldValidate: true });
                        triggerValidation('contactType');
                        triggerValidation('contactPreferedMethod');

                        return value;
                      }}
                      data-track="contactType"
                      data-track-value="capture"
                    />
                  )}
                />
              </>
            )}

            {showContactMethod && (
              <>
                <FormSpacer type="input" />

                <Controller
                  name="contactPreferedMethod"
                  control={control}
                  render={({ field }) => {
                    return (
                      <ContactPreferredMethod
                        required
                        {...field}
                        name="contactPreferedMethod"
                        title={getTextContent('consent.contactDetails.preferedContactType')}
                        titleSuffix={
                          (
                            <InformationPopUp
                              message={getTextContent('consent.contactDetails.preferedContactType.infoText')}
                              dataTrack="infoIconPreferredContact"
                            />
                          ) as any
                        }
                        onChange={(method: string, fields) => {
                          setValue('contactPreferedMethod', method);

                          fields.forEach((field: string) => {
                            if (get(values, field) && get(values, field).length > 0) {
                              triggerValidation(field as any);
                            } else {
                              clearError(field as any);
                            }

                            return field;
                          });

                          if (method !== 'post') {
                            setValue('isManualAddressEnabled', false);
                            clearError('postalAddress');
                            setValue('isPostalAddressValid', false);
                            setValue('country', null);
                            setValue('postalAddressObject', null);
                          }
                        }}
                      />
                    );
                  }}
                />
              </>
            )}

            {showContactMethod && contactPreferedMethod === 'post' && (
              <>
                <FormGroupLabel
                  alignCenter
                  legendElementOveride
                  required={isFieldRequired('postalAddress')}
                  title={getTextContent('consent.contactDetails.postalAddress')}
                />
                <FormSpacer type="input" />

                {DcomPrtrQasMigrationToggle ? (
                  <>
                    <CmsAddressSearch
                      type={AddressType.Postal}
                      required={true}
                      onAddressChange={handlePostalAddressChange}
                      getAddressDefaults={getPostalAddressDefaults}
                    />
                  </>
                ) : (
                  <>
                    {!isManualAddressEnabled && (
                      <AddressAutocomplete
                        form={form}
                        label="Postal Address"
                        fieldName="postalAddress"
                        fieldNameIsValid="isPostalAddressValid"
                        fieldNameIsManual="isManualAddressEnabled"
                        linkType="postal"
                        dataTrack="postalAddress"
                        dataTrackValue="mask"
                        addressFieldNames={{
                          country: 'country',
                          streetAddress: 'streetAddress',
                          suburb: 'suburb',
                          state: 'state',
                          postCode: 'postcode',
                        }}
                      />
                    )}
                    {isManualAddressEnabled && (
                      <AddressManual
                        form={form}
                        slotProps={{
                          streetAddress: {
                            'data-track': 'manualStreetAddress',
                          },
                          suburb: {
                            'data-track': 'manualsuburb',
                          },
                          state: {
                            'data-track': 'manualState',
                          },
                          postcode: {
                            'data-track': 'manualPostcode',
                          },
                        }}
                      />
                    )}
                  </>
                )}
              </>
            )}

            {showContactNextButton && personalDetails && contactDetails && (
              <>
                <FormSpacer type="formgroup" />

                <Button type="submit" label="Continue" disabled={!isValid} data-track="continue" />
              </>
            )}
          </FormCard>
        </>
      )}
    </Form>
  );
};

export default ConsentForm;
